import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { InlineDatePicker, Period } from 'components/Form/InlineDatePicker/InlineDatePicker';
import { LeftColumn } from 'components/Layout/TwoColumns/LeftColumn/LeftColumn';
import { RightColumn } from 'components/Layout/TwoColumns/RightColumn/RightColumn';
import { TwoColumnsLayout } from 'components/Layout/TwoColumns/TwoColumnsLayout';
import { MarketplaceInfo } from 'components/Marketplace/Info/MarketplaceInfo';
import { Alert } from 'components/UI/Alert/Alert';
import { useMarketplace } from 'hooks/useMarketplace';
import { useTimeZone } from 'hooks/useTimeZone';
import moment, { Moment } from 'moment-timezone';
import React from 'react';
import { useSelector } from 'react-redux';
import { getOlbDateLimits } from 'reducers/masterData/MasterDataSelectors';
import { dispatch } from 'store';
import { getOnlineBookingBoardingAvailabilityThunk } from 'actions/onlineBooking/actions/OnlineBookingAvailabilityActions';
import {
    navigateToStep,
    updateOnlineBookingBookingItems
} from 'actions/onlineBooking/actions/OnlineBookingFlowActions';
import { OnlineBookingFooter } from 'components/OnlineBooking/Footer/OnlineBookingFooter';
import { JoinWaitlistCta } from 'components/OnlineBooking/JoinWaitlistCta/JoinWaitlistCta';
import { OnlineBookingHeader } from 'components/OnlineBooking/Header/OnlineBookingHeader';
import { OnlineBookingResume } from 'components/OnlineBooking/Resume/OnlineBookingResume';
import {
    getOnlineBookingBoardingAvailability,
    getOnlineBookingBoardingAvailabilityLoading,
    getOnlineBookingBoardingDate,
    getOnlineBookingBoardingItems
} from 'reducers/onlineBooking/OnlineBookingSelectors';
import { MarketplaceServiceCategory } from 'types/Marketplace';
import { OnlineBookingStep } from 'types/OnlineBooking';
import { assignBoardingUnitsForPets } from 'utils/boardingUtils';
import { getMarketplaceBusinessHoursForDate } from 'utils/marketplaceScheduleUtils';
import { resetBoardingAddonsOnStayChange } from 'utils/onlineBookingAddons';

export const OnlineBookingBoardingDatePickerStep: React.FC = () => {
    const timeZone = useTimeZone();
    const marketplace = useMarketplace();

    const olbDateLimits = useSelector(getOlbDateLimits);
    const limitPreferences = marketplace.onlineBookingPreferences.bookingDateLimits;

    const selectedPeriod = useSelector(getOnlineBookingBoardingDate);
    const boardingBookingItems = useSelector(getOnlineBookingBoardingItems);
    const boardingAvailability = useSelector(getOnlineBookingBoardingAvailability);
    const boardingAvailabilityLoading = useSelector(getOnlineBookingBoardingAvailabilityLoading);

    const today = moment().tz(timeZone);

    const selectedService = boardingBookingItems[0]?.service;
    const minNights = selectedService?.boardingMetadata?.minNights ?? 1;

    const minDate = React.useMemo(
        () =>
            today
                .clone()
                .add(
                    olbDateLimits.from?.find(limit => limit.id === limitPreferences.from)
                        ?.offsetSeconds ?? 0,
                    'seconds'
                ),
        [today, olbDateLimits, limitPreferences.from]
    );

    const maxDate = React.useMemo(
        () =>
            today
                .clone()
                .add(
                    olbDateLimits.to?.find(limit => limit.id === limitPreferences.to)
                        ?.offsetSeconds ?? 0,
                    'seconds'
                ),
        [today, olbDateLimits, limitPreferences.to]
    );

    const nightsCount = React.useMemo(() => {
        if (!selectedPeriod?.from || !selectedPeriod?.to) {
            return 0;
        }

        return selectedPeriod.to.diff(selectedPeriod.from, 'days');
    }, [selectedPeriod]);

    const meetsMinNights = nightsCount >= minNights;

    const showMinNightsAlert = !!selectedPeriod?.from && !!selectedPeriod?.to && !meetsMinNights;

    const isSelectedPeriodAvailabilityLoaded = React.useMemo(() => {
        if (!selectedPeriod?.from || !boardingAvailability) {
            return false;
        }

        return boardingAvailability.date.isSame(selectedPeriod.from, 'day');
    }, [boardingAvailability, selectedPeriod]);

    const availabilityRooms = React.useMemo(() => {
        if (!isSelectedPeriodAvailabilityLoaded) {
            return [];
        }

        return boardingAvailability?.dropOffSlots[0]?.pickUpSlots[0]?.rooms ?? [];
    }, [boardingAvailability, isSelectedPeriodAvailabilityLoaded]);

    const assignedRoomUnitIds = React.useMemo(() => {
        if (availabilityRooms.length === 0 || boardingBookingItems.length === 0) {
            return null;
        }

        return assignBoardingUnitsForPets(
            availabilityRooms,
            boardingBookingItems.map(item => item.pet)
        );
    }, [availabilityRooms, boardingBookingItems]);

    const hasAvailableSlots =
        isSelectedPeriodAvailabilityLoaded &&
        availabilityRooms.length > 0 &&
        assignedRoomUnitIds !== null;

    const shouldShowNoAvailabilityAlert =
        meetsMinNights &&
        !boardingAvailabilityLoading &&
        isSelectedPeriodAvailabilityLoaded &&
        !hasAvailableSlots;

    const isDateDisabled = React.useCallback(
        (calendarDate: Date) =>
            getMarketplaceBusinessHoursForDate(
                marketplace,
                moment(calendarDate).tz(timeZone, true)
            ) === null,
        [marketplace, timeZone]
    );

    const getDisabledDateTooltip = React.useCallback(
        (calendarDate: Date) =>
            isDateDisabled(calendarDate)
                ? `Marketplace closed on ${moment(calendarDate)
                      .tz(timeZone, true)
                      .format('MMM D, YYYY')}`
                : undefined,
        [isDateDisabled, timeZone]
    );

    const getClosedDaysInRange = React.useCallback(
        (from: Moment, to: Moment): Array<string> => {
            const closed: Array<string> = [];
            const cursor = from.clone();

            while (cursor.isSameOrBefore(to, 'day')) {
                if (getMarketplaceBusinessHoursForDate(marketplace, cursor) === null) {
                    closed.push(cursor.format('MMM D, YYYY'));
                }
                cursor.add(1, 'day');
            }

            return closed;
        },
        [marketplace]
    );

    const closedDaysInSelectedRange = React.useMemo(() => {
        if (!selectedPeriod?.from || !selectedPeriod?.to) {
            return [];
        }

        return getClosedDaysInRange(selectedPeriod.from, selectedPeriod.to);
    }, [selectedPeriod, getClosedDaysInRange]);

    const hasClosedDaysInRange = closedDaysInSelectedRange.length > 0;

    // Once the pet parent leaves the step, by any way out, a stay that changed since the Add-Ons
    // were set restores them to the default. The clicks while picking a range never count.
    const latestBoardingBookingItems = React.useRef(boardingBookingItems);
    latestBoardingBookingItems.current = boardingBookingItems;

    React.useEffect(
        () => () => {
            const items = latestBoardingBookingItems.current;
            const updatedItems = items.map(resetBoardingAddonsOnStayChange);

            if (updatedItems.some((item, index) => item !== items[index])) {
                dispatch(updateOnlineBookingBookingItems(updatedItems));
            }
        },
        []
    );

    const backHandler = () => {
        dispatch(navigateToStep(OnlineBookingStep.ServiceList));
    };

    const dateChangeHandler = async (period: Period | null) => {
        if (!period) {
            return;
        }

        const updatedItems = boardingBookingItems.map(item => ({
            ...item,
            startDate: period.from,
            endDate: period.to,
            checkInTime: undefined,
            checkOutTime: undefined,
            roomUnitId: undefined
        }));

        dispatch(updateOnlineBookingBookingItems(updatedItems));

        if (!period.from || !period.to || !selectedService) {
            return;
        }

        const stayNights = period.to.diff(period.from, 'days');

        if (stayNights < minNights) {
            return;
        }

        if (getClosedDaysInRange(period.from, period.to).length > 0) {
            return;
        }

        try {
            await dispatch(
                getOnlineBookingBoardingAvailabilityThunk(period.from.clone(), period.to.clone())
            );
        } catch {
            // Leave continue disabled if availability could not be loaded.
        }
    };

    const submitHandler = () => {
        dispatch(navigateToStep(OnlineBookingStep.TimePicker));
    };

    return (
        <TwoColumnsLayout header={<OnlineBookingHeader onBack={backHandler} />}>
            <LeftColumn>
                <h1 className="h1 w-full underlined mb-5">Select days</h1>

                <InlineDatePicker
                    rangeMode
                    minDate={minDate}
                    maxDate={maxDate}
                    timeZone={timeZone}
                    value={selectedPeriod}
                    isDateDisabled={isDateDisabled}
                    getDisabledDateTooltip={getDisabledDateTooltip}
                    onChange={dateChangeHandler}
                />

                {showMinNightsAlert && (
                    <div className="mt-5">
                        <Alert
                            variant="warning"
                            icon={faInfoCircle}
                            text={`This room requires a minimum of ${minNights} nights. Please select a longer stay.`}
                        />
                    </div>
                )}

                {hasClosedDaysInRange && (
                    <div className="mt-5">
                        <Alert
                            variant="warning"
                            icon={faInfoCircle}
                            text={`Marketplace closed on ${closedDaysInSelectedRange.join(
                                ', '
                            )}. Please choose a range that doesn't cross closed days.`}
                        />
                    </div>
                )}

                {!hasClosedDaysInRange && shouldShowNoAvailabilityAlert && (
                    <div className="mt-5">
                        <Alert
                            variant="warning"
                            icon={faInfoCircle}
                            text="No availability for the selected dates. Try a different range."
                        />
                    </div>
                )}

                <JoinWaitlistCta category={MarketplaceServiceCategory.BOARDING} />
            </LeftColumn>

            <RightColumn
                poweredByCuddles
                footer={
                    <OnlineBookingFooter
                        label="Continue"
                        loading={boardingAvailabilityLoading}
                        disabled={
                            !selectedPeriod ||
                            !meetsMinNights ||
                            hasClosedDaysInRange ||
                            !hasAvailableSlots
                        }
                        onSubmit={submitHandler}
                    />
                }
            >
                <div className="flex flex-col gap-5 hide-on-mobile-and-tablet">
                    <MarketplaceInfo inverted marketplace={marketplace} />

                    <OnlineBookingResume />
                </div>
            </RightColumn>
        </TwoColumnsLayout>
    );
};
